import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, Check, ChevronRight, Copy, Inbox, Loader2, Mail } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";

type GetUserResponse = components["schemas"]["GetUserResponse"];
type OrganisationResponse = components["schemas"]["OrganisationResponse"];
type OrganisationRole = components["schemas"]["OrganisationRole"];

const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" });

function roleClasses(role: OrganisationRole) {
  if (role === "Owner") return "border-secondary/50 bg-secondary/15 text-secondary-foreground";
  return "border-border bg-muted";
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<GetUserResponse | null>(null);
  const [orgs, setOrgs] = useState<Map<string, OrganisationResponse>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUser(null);
    setOrgs(new Map());

    (async () => {
      const userResult = await api.GET("/v1/users/{id}", { params: { path: { id } } });
      if (cancelled) return;
      if (userResult.error || !userResult.data) {
        setError(userResult.response?.status === 404 ? "User not found." : "Couldn't load this user.");
        setLoading(false);
        return;
      }
      setUser(userResult.data);

      const orgIds = (userResult.data.organisations ?? [])
        .map((o) => o.organisationId)
        .filter((x): x is string => !!x);

      const orgResults = await Promise.all(
        orgIds.map((orgId) => api.GET("/v1/organisations/{id}", { params: { path: { id: orgId } } })),
      );
      if (cancelled) return;
      const map = new Map<string, OrganisationResponse>();
      for (const r of orgResults) {
        if (r.data?.id) map.set(r.data.id, r.data);
      }
      setOrgs(map);
      setLoading(false);
    })().catch(() => {
      if (!cancelled) {
        setError("Network error loading user.");
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [id]);

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <Breadcrumbs name={user?.name ?? user?.email} />

      {loading ? (
        <LoadingState />
      ) : error || !user ? (
        <ErrorState message={error ?? "User not found."} />
      ) : (
        <>
          <header className="flex flex-col gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold">{user.name ?? user.email}</h1>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="size-3.5" />
              <a href={`mailto:${user.email}`} className="hover:text-foreground hover:underline">
                {user.email}
              </a>
            </div>
            {user.id && <IdCopyRow value={user.id} />}
          </header>

          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
              <Field label="Name" value={user.name ?? null} />
              <Field label="Email" value={user.email} />
              <Field label="Created" value={user.createdAt ? dateFmt.format(new Date(user.createdAt)) : null} />
            </CardContent>
          </Card>

          <section className="flex flex-col gap-3">
            <div className="flex items-baseline gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Organisations</h2>
              <span className="text-xs text-muted-foreground tabular-nums">{user.organisations?.length ?? 0}</span>
            </div>
            {!user.organisations || user.organisations.length === 0 ? (
              <Empty>This user isn't a member of any organisations.</Empty>
            ) : (
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40">
                      <TableHead>Organisation</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {user.organisations.map((membership) => {
                      const orgId = membership.organisationId;
                      const org = orgId ? orgs.get(orgId) : undefined;
                      const name = org?.name ?? orgId ?? "—";
                      return (
                        <TableRow key={orgId ?? name}>
                          <TableCell>
                            {orgId ? (
                              <Link to={`/organisations/${orgId}`} className="flex flex-col hover:underline">
                                <span className="font-medium">{name}</span>
                                <span className="font-mono text-xs text-muted-foreground">{orgId}</span>
                              </Link>
                            ) : (
                              <span className="font-medium">{name}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={roleClasses(membership.role)}>{membership.role}</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {membership.joinedAt ? dateFmt.format(new Date(membership.joinedAt)) : "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function IdCopyRow({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="inline-flex w-fit items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-1">
      <span className="font-mono text-xs text-muted-foreground">{value}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={copy}
            className="text-muted-foreground hover:text-foreground"
          >
            {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{copied ? "Copied!" : "Copy user ID"}</TooltipContent>
      </Tooltip>
    </div>
  );
}

function Breadcrumbs({ name }: { name?: string | null }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <Link to="/organisations" className="rounded-sm hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        Organisations
      </Link>
      <ChevronRight className="size-3.5" />
      <span className="truncate text-foreground">{name ?? "…"}</span>
    </nav>
  );
}

function Field({ label, value }: { label: string; value?: null | string }) {
  const empty = value == null || value === "";
  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      {empty ? (
        <div className="text-sm text-muted-foreground">—</div>
      ) : (
        <div className="text-sm truncate">{value}</div>
      )}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border py-16 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" /> Loading user…
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
      <AlertTriangle className="size-4 mt-0.5" />
      <span>{message}</span>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
      <Inbox className="size-6" />
      {children}
    </div>
  );
}
